import { useEffect, type FC } from 'react';
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
  useNavigate,
} from 'react-router-dom';

import DiscoverPage from './components/DiscoverPage';
import LoginPage from './components/LoginPage';
import MapShell from './components/MapShell';
import RideDetailPage from './components/RideDetailPage';
import RideListPage from './components/RideListPage';
import RouteDetailPage from './components/RouteDetailPage';
import RouteEditorPage from './components/RouteEditorPage';
import RouteLibraryPage from './components/RouteLibraryPage';
import RoutePlannerPage from './components/RoutePlannerPage';
import UpdatePrompt from './components/UpdatePrompt';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { DiscoverProvider } from './hooks/useDiscover';
import { RideRecorderProvider } from './hooks/useRideRecorder';
import { RideReviewProvider } from './hooks/useRideReview';
import { RouteEditorProvider } from './hooks/useRouteEditor';
import { RoutePlanProvider } from './hooks/useRoutePlan';
import { SettingsProvider } from './hooks/useSettings';
import { VoiceProvider } from './hooks/useVoice';

// Desktop (hover + fine pointer — not screen width, which misfires on tablets and small
// windows) lands on Discover: at a desk you browse routes, on the bike you glance at the
// map. Only when the page load itself was at "/", and only once — in-app navigation back
// to the map, and deep links to other screens, are untouched. An effect (not a render-time
// <Navigate>) so StrictMode's double render can't drop the redirect: the flag makes the
// second effect invocation a no-op instead of the second render returning null.
const openedAtRoot = window.location.pathname === '/';
let landingHandled = false;
const IndexLanding: FC = () => {
  const navigate = useNavigate();
  useEffect(() => {
    if (landingHandled) return;
    landingHandled = true;
    if (openedAtRoot && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      navigate('/discover', { replace: true });
    }
  }, [navigate]);
  return null;
};

const AuthGate: FC = () => {
  const { state, refresh } = useAuth();

  if (state.status === 'loading') {
    return (
      <div className="mg-splash">
        <div className="mg-splash-logo">MotoGo</div>
      </div>
    );
  }
  if (state.status === 'error') {
    // Transient API hiccup at launch shouldn't strand the user — let them retry
    // without hunting for the browser reload.
    return (
      <div className="mg-splash">
        <div className="mg-splash-logo" style={{ animation: 'none' }}>MotoGo</div>
        <div className="mg-splash-text">Connection failed: {state.error}</div>
        <button
          className="mg-btn mg-btn-primary"
          style={{ width: 'auto', padding: '12px 28px' }}
          onClick={() => void refresh()}
        >
          Retry
        </button>
      </div>
    );
  }
  if (state.status === 'unauthenticated') {
    return <LoginPage />;
  }
  // 'authenticated' or 'guest' -- both surface the map. Feature gates (voice etc.)
  // check the auth state themselves and prompt the guest to sign in if needed.
  // The data router (createBrowserRouter) is what enables useBlocker in the route editor —
  // the hardware/browser back button drives popstate, and a dirty editing session must be
  // able to intercept it instead of silently discarding the route.
  return <RouterProvider router={router} />;
};

// The providers live in a top layout route (not wrapping <RouterProvider>) so they render
// *inside* the router context and stay mounted across every navigation — the voice/location
// session, settings, and the persistent map survive moving between screens. VoiceProvider
// owns the voice + location-share session (VoiceControls drives it, MapView feeds positions
// in and renders the other riders). MapShell is the nested layout that mounts the map once
// and renders ride/route screens as overlays via its <Outlet/>; the index route is empty so
// the map (with its chrome) is what shows at "/".
const AppLayout: FC = () => (
  <SettingsProvider>
    <VoiceProvider>
      <RideReviewProvider>
        <RoutePlanProvider>
          <RouteEditorProvider>
            <DiscoverProvider>
              <RideRecorderProvider>
                <Outlet />
              </RideRecorderProvider>
            </DiscoverProvider>
          </RouteEditorProvider>
        </RoutePlanProvider>
      </RideReviewProvider>
    </VoiceProvider>
  </SettingsProvider>
);

const router = createBrowserRouter([
  {
    element: <AppLayout />,
    children: [
      {
        element: <MapShell />,
        children: [
          { index: true, element: <IndexLanding /> },
          { path: 'route', element: <RoutePlannerPage /> },
          { path: 'rides', element: <RideListPage /> },
          { path: 'rides/:id', element: <RideDetailPage /> },
          { path: 'routes', element: <RouteLibraryPage /> },
          { path: 'routes/new', element: <RouteEditorPage /> },
          { path: 'routes/:id', element: <RouteDetailPage /> },
          { path: 'routes/:id/edit', element: <RouteEditorPage /> },
          { path: 'discover', element: <DiscoverPage /> },
          { path: '*', element: <Navigate to="/" replace /> },
        ],
      },
    ],
  },
]);

const App: FC = () => (
  <AuthProvider>
    {/* Above the gate: an update should be offered on the login page too. */}
    <UpdatePrompt />
    <AuthGate />
  </AuthProvider>
);

export default App;
